<script setup lang="ts">
import { useUiStore } from '@/stores/ui'

const ui = useUiStore()

const variantBorderClass: Record<string, string> = {
  success: 'border-l-sf-success',
  error: 'border-l-sf-danger',
  info: 'border-l-sf-accent',
}

const variantIconColor: Record<string, string> = {
  success: 'text-sf-success',
  error: 'text-sf-danger',
  info: 'text-sf-accent',
}

function removeToast(id: string) {
  ui.removeToast(id)
}
</script>

<template>
  <Teleport to="body">
    <div class="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 pointer-events-none">
      <TransitionGroup name="toast-slide">
        <div
          v-for="toast in ui.toasts"
          :key="toast.id"
          :class="[
            'pointer-events-auto flex items-center gap-3 min-w-[320px] max-w-[440px] px-4 py-3.5 bg-sf-bg-secondary border border-sf-border rounded-[12px] shadow-lg border-l-[3px]',
            variantBorderClass[toast.variant],
          ]"
        >
          <!-- Icon -->
          <div :class="['flex-shrink-0', variantIconColor[toast.variant]]">
            <!-- Success checkmark -->
            <svg
              v-if="toast.variant === 'success'"
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <!-- Error X -->
            <svg
              v-else-if="toast.variant === 'error'"
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <!-- Info circle -->
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>

          <!-- Message -->
          <p class="flex-1 text-sm text-sf-text-primary">{{ toast.message }}</p>

          <!-- Close button -->
          <button
            class="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded text-sf-text-muted hover:text-sf-text-primary transition-colors"
            @click="removeToast(toast.id)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-slide-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-slide-enter-from {
  opacity: 0;
  transform: translateX(80px);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(80px);
}

.toast-slide-move {
  transition: transform 0.3s ease;
}
</style>
